/**
 * Pill category carousel — mouse + touch drag, arrows
 */
class AdawatPillCarousel extends HTMLElement {
  connectedCallback() {
    if (this._adawatBooted) return;
    const boot = () => this.boot();
    if (window.AdawatLazyInit?.whenVisible) {
      window.AdawatLazyInit.whenVisible(this, boot);
    } else {
      boot();
    }
  }

  boot() {
    if (this._adawatBooted) return;
    this._adawatBooted = true;

    this.viewport = this.querySelector('[data-pill-viewport]');
    if (!this.viewport) return;

    this.prevBtn = this.querySelector('[data-pill-prev]');
    this.nextBtn = this.querySelector('[data-pill-next]');

    this.dragging = false;
    this.dragMoved = false;
    this.axisLocked = null;
    this.startX = 0;
    this.startY = 0;
    this.startScroll = 0;
    this.pointerId = null;

    this.prevBtn?.addEventListener('click', () => this.scrollByDir(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByDir(1));
    this.viewport.addEventListener('scroll', () => this.updateUI(), { passive: true });
    this.onResize = () => this.updateUI();
    window.addEventListener('resize', this.onResize);

    this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), {
      passive: false,
    });
    this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
    this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    this.viewport.addEventListener('lostpointercapture', (event) => this.onPointerUp(event));

    this.viewport.addEventListener(
      'click',
      (event) => {
        if (this.dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          this.dragMoved = false;
        }
      },
      true
    );

    this.updateUI();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  canScroll() {
    return this.viewport.scrollWidth > this.viewport.clientWidth + 2;
  }

  getMaxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  getStep() {
    const pill = this.viewport.querySelector('[data-pill-item]');
    if (!pill) return Math.round(this.viewport.clientWidth * 0.55);
    const track = this.viewport.querySelector('[data-pill-track]');
    const gap = parseFloat(getComputedStyle(track).gap) || 10;
    return Math.round(pill.getBoundingClientRect().width + gap);
  }

  scrollByDir(direction) {
    const step = this.getStep() * 2;
    const isRtl = document.documentElement.dir === 'rtl';
    const delta = (isRtl ? -direction : direction) * step;
    this.viewport.scrollBy({ left: delta, behavior: 'smooth' });
  }

  updateUI() {
    const max = this.getMaxScroll();
    const left = Math.abs(this.viewport.scrollLeft);
    const edge = 2;
    if (this.prevBtn) this.prevBtn.disabled = !this.canScroll() || left <= edge;
    if (this.nextBtn) this.nextBtn.disabled = !this.canScroll() || left >= max - edge;
  }

  onPointerDown(event) {
    if (!this.canScroll()) return;
    // Touch/pen: native overflow scroll so vertical page scroll works.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('.adawat-pill-carousel__arrow')) return;

    this.dragging = true;
    this.dragMoved = false;
    this.axisLocked = 'x';
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startScroll = this.viewport.scrollLeft;
    this.pointerId = event.pointerId;
    this.viewport.classList.add('is-grabbing');

    try {
      this.viewport.setPointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;

    if (this.axisLocked == null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      this.axisLocked = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (this.axisLocked === 'y') {
        this.dragging = false;
        this.pointerId = null;
        this.viewport.classList.remove('is-grabbing');
        return;
      }
      try {
        this.viewport.setPointerCapture?.(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }

    if (this.axisLocked !== 'x') return;

    if (!this.dragMoved && Math.abs(dx) > 6) {
      this.dragMoved = true;
      this.viewport.classList.add('is-dragging');
    }

    if (!this.dragMoved) return;

    this.viewport.scrollLeft = this.startScroll - dx;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragging || (this.pointerId != null && event.pointerId !== this.pointerId)) return;

    this.dragging = false;
    this.axisLocked = null;
    this.pointerId = null;
    this.viewport.classList.remove('is-dragging', 'is-grabbing');

    try {
      this.viewport.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }

    this.updateUI();

    if (!this.dragMoved) return;
    window.setTimeout(() => {
      this.dragMoved = false;
    }, 0);
  }
}

if (!customElements.get('adawat-pill-carousel')) {
  customElements.define('adawat-pill-carousel', AdawatPillCarousel);
}
